import React from 'react';
import { cx } from './cx';
import { focusRing } from './focusRing';
import { playClickSound } from '@/utils/soundUtils';

export interface FilterChipItem<T extends string> {
  value: T;
  label: React.ReactNode;
}

export interface FilterChipsProps<T extends string> {
  items: FilterChipItem<T>[];
  value: T | T[];
  onChange: (value: T | T[]) => void;
  multiple?: boolean;
  'aria-label': string;
  className?: string;
}

/**
 * The chip look, shared with the single `FilterChip`: 32px, `pill` (the style's chip corners,
 * rounded-chip) or `box` (rounded-control, like the toolbar controls beside it); idle = the chip
 * fill and the secondary Button's edge, selected = focus-tinted border on the selected surface.
 * The font family is left to the caller (`font-ui` for labels, `font-money` for amounts).
 */
export function filterChipClass(selected: boolean, shape: 'pill' | 'box' = 'pill'): string {
  return cx(
    'inline-flex h-8 items-center border px-3.5 text-small font-medium transition-colors duration-(--k-dur-fast)',
    shape === 'box' ? 'rounded-control' : 'rounded-chip',
    focusRing,
    selected
      ? 'border-focus/60 bg-selected text-primary'
      : 'border-btn-secondary-edge bg-chip-fill text-secondary hover:border-edge-strong hover:text-primary'
  );
}

/** Row of toggle chips (single or multiple selection). */
export default function FilterChips<T extends string>({ items, value, onChange, multiple = false, 'aria-label': ariaLabel, className }: FilterChipsProps<T>) {
  const selected = new Set(Array.isArray(value) ? value : [value]);
  return (
    <div role="group" aria-label={ariaLabel} className={cx('flex flex-wrap gap-2', className)}>
      {items.map((item) => {
        const on = selected.has(item.value);
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={on}
            onClick={() => {
              playClickSound();
              if (!multiple) return onChange(item.value);
              const next = new Set(selected);
              if (on) next.delete(item.value);
              else next.add(item.value);
              onChange(items.map((i) => i.value).filter((v) => next.has(v)));
            }}
            className={cx(filterChipClass(on), 'font-ui')}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
